import { Box, Divider, Group, Stack, Text } from '@mantine/core';
import { ContentCard } from '../../../components/common/ContentCard';
import { BarChart2 } from 'react-feather';

// MED-1059 — match the main-column cards' icon + title header (AC1).
const TITLE_COLOR = '#1A1A2E';
import type {
  FeedbackSidebarStatus,
  PostSessionSidebarSessionSummary,
} from '../../../services/api/participantPostSession';

interface Props {
  summary: PostSessionSidebarSessionSummary;
  /** Live override — driven by autosave/submit on the feedback card. */
  liveFeedbackStatus?: FeedbackSidebarStatus;
}

/**
 * Session Summary card for the right sidebar (MED-806 AC15).
 *
 * Renders: Duration, Session number ("N of M"), Led by, Your feedback
 * status — labeled "Your feedback" per AC7 (NOT "Your reflection").
 *
 * `liveFeedbackStatus` lets the parent page push real-time feedback status
 * changes into the card without refetching (IV2).
 */
export function SessionSummarySidebarCard({
  summary,
  liveFeedbackStatus,
}: Props) {
  const status = liveFeedbackStatus ?? summary.feedback_status;
  const statusLabel =
    status === 'submitted'
      ? 'Complete'
      : status === 'in_progress'
        ? 'In progress'
        : 'Not started';
  const statusColor =
    status === 'submitted'
      ? 'medleyGreen'
      : status === 'in_progress'
        ? 'warningAmber'
        : 'dimmed';

  const durationLine =
    summary.duration_minutes != null &&
    summary.scheduled_duration_minutes != null
      ? `${summary.duration_minutes} min (of ${summary.scheduled_duration_minutes})`
      : summary.duration_minutes != null
        ? `${summary.duration_minutes} min`
        : '—';

  const sessionNumberLine =
    summary.session_number != null && summary.total_sessions != null
      ? `${summary.session_number} of ${summary.total_sessions}`
      : summary.session_number != null
        ? `${summary.session_number}`
        : '—';

  return (
    <ContentCard
      variant="elevated"
      padding="lg"
      fill={false}
      data-testid="sidebar-session-summary"
      header={
        <Group gap={8} align="center" wrap="nowrap">
          <BarChart2
            size={18}
            color={TITLE_COLOR}
            aria-hidden="true"
            style={{ marginTop: -4 }}
          />
          <Text fz={15} fw={700} c={TITLE_COLOR}>
            Session Summary
          </Text>
        </Group>
      }
    >
      <Stack gap="sm">
        <Box>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Duration
            </Text>
            <Text size="sm" fw={600} data-testid="sidebar-duration">
              {durationLine}
            </Text>
          </Group>
        </Box>
        <Divider />
        <Box>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Session number
            </Text>
            <Text size="sm" fw={600} data-testid="sidebar-session-number">
              {sessionNumberLine}
            </Text>
          </Group>
        </Box>
        <Divider />
        <Box>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Led by
            </Text>
            <Text size="sm" fw={600} data-testid="sidebar-leader-name">
              {summary.leader_display_name || '—'}
            </Text>
          </Group>
        </Box>
        <Divider />
        <Box>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Your feedback
            </Text>
            <Text
              size="sm"
              fw={600}
              c={statusColor}
              data-testid="sidebar-feedback-status"
              data-status={status}
            >
              {statusLabel}
            </Text>
          </Group>
        </Box>
      </Stack>
    </ContentCard>
  );
}
